import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  Save, Sparkles, ChevronDown, ChevronUp, Loader2, AlertTriangle,
  Info, Brain, TrendingUp, TrendingDown, Flag, Lightbulb, Plus, Trash2, Users
} from 'lucide-react';
import axios from 'axios';

import { API_URL } from '../../utils/api';

const EVALUATOR_ROLES = [
  'School Psychologist', 'SLP', 'Special Education Teacher',
  'Occupational Therapist', 'Physical Therapist', 'General Education Teacher',
  'Board Certified Behavior Analyst', 'Other',
];

function mergeContent(existing) {
  const defaults = { evaluator_interpretations: [], interpretation_text: '' };
  if (!existing || typeof existing !== 'object') return defaults;
  // Migration: if old single-text format, put it into interpretation_text
  if (existing.notes && !existing.interpretation_text && !existing.evaluator_interpretations) {
    return { ...defaults, interpretation_text: existing.notes };
  }
  return { ...defaults, ...existing };
}

export default function InterpretationEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  const [helperOpen, setHelperOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [helperData, setHelperData] = useState(null);
  const [helperMeta, setHelperMeta] = useState(null);

  useEffect(() => { setContent(mergeContent(section?.content)); }, [section?.section_id]);

  const handleSave = () => onSave(content);
  const interpretations = content.evaluator_interpretations || [];

  const addInterpretation = () => {
    setContent(prev => ({
      ...prev,
      evaluator_interpretations: [
        ...(prev.evaluator_interpretations || []),
        { id: `interp_${Date.now()}`, name: '', role: '', interpretation: '' },
      ],
    }));
  };

  const removeInterpretation = (idx) => {
    setContent(prev => ({
      ...prev,
      evaluator_interpretations: prev.evaluator_interpretations.filter((_, i) => i !== idx),
    }));
  };

  const updateInterpretation = (idx, field, value) => {
    setContent(prev => ({
      ...prev,
      evaluator_interpretations: prev.evaluator_interpretations.map((item, i) =>
        i === idx ? { ...item, [field]: value } : item
      ),
    }));
  };

  const runHelper = async () => {
    setAnalyzing(true);
    setHelperData(null);
    setHelperMeta(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/iep-met/cases/${caseData.case_id}/met-sections/met_interpretation/draft`,
        {}, { withCredentials: true }
      );
      const output = res.data?.output;
      if (output) {
        setHelperData(output);
        setHelperMeta({
          dataSources: output.data_sources_used || [],
          notes: output.notes || '',
          model: res.data?.model_used,
        });
      }
    } catch (err) {
      console.error('AI helper failed', err);
      setHelperMeta({ error: err.response?.data?.detail || 'AI analysis failed. Please try again.' });
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="interpretation-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="interpretation-title">Interpretation of Assessment Results</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          <Badge className="bg-[#5A8A5A]/10 text-[#5A8A5A] text-xs mt-2">{interpretations.length} evaluator{interpretations.length !== 1 ? 's' : ''}</Badge>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl shrink-0" data-testid="interpretation-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* AI Helper Panel */}
      <div className="bg-gradient-to-r from-[#5A8A5A]/5 to-[#3B6A9B]/5 border border-[#5A8A5A]/20 rounded-2xl overflow-hidden" data-testid="ai-interpretation-helper">
        <button className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/40 transition-colors" onClick={() => setHelperOpen(!helperOpen)} data-testid="interpretation-helper-toggle">
          <div className="flex items-center gap-2.5">
            <Sparkles className="h-4 w-4 text-[#5A8A5A]" />
            <span className="text-sm font-medium text-[#1C1C1C]">AI Interpretation Helper</span>
            <Badge className="text-[10px] bg-[#5A8A5A]/10 text-[#5A8A5A] border border-[#5A8A5A]/20">Helper Only</Badge>
          </div>
          {helperOpen ? <ChevronUp className="h-4 w-4 text-[#595959]" /> : <ChevronDown className="h-4 w-4 text-[#595959]" />}
        </button>

        {helperOpen && (
          <div className="px-5 pb-5 border-t border-[#5A8A5A]/10">
            <div className="flex items-center justify-between mt-3 mb-4">
              <p className="text-xs text-[#595959] max-w-lg">
                AI analyzes assessment data to surface patterns, themes, and caution flags. Use these as prompts — you author the interpretation.
              </p>
              <Button onClick={runHelper} disabled={analyzing}
                className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl text-xs h-8 shrink-0" data-testid="run-interpretation-helper-btn">
                {analyzing ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Analyzing...</>
                  : <><Sparkles className="h-3 w-3 mr-1.5" />{helperData ? 'Re-analyze' : 'Analyze Data'}</>}
              </Button>
            </div>

            {helperMeta?.error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="interpretation-helper-error">
                <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{helperMeta.error}</p>
              </div>
            )}

            {helperData && (
              <div className="space-y-4" data-testid="interpretation-helper-results">
                {helperData.interpretation_points?.length > 0 && (
                  <div className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid="helper-interpretation-points">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-2 flex items-center gap-1.5">
                      <Brain className="h-3.5 w-3.5 text-[#5A8A5A]" />Key Interpretation Points
                    </h4>
                    <div className="space-y-2">
                      {helperData.interpretation_points.map((p, i) => (
                        <div key={i} className="border-l-2 border-[#5A8A5A]/30 pl-3 py-1">
                          <p className="text-xs font-medium text-[#1C1C1C]">{p.area}</p>
                          <p className="text-xs text-[#595959]">{p.finding}</p>
                          {p.significance && <p className="text-[10px] text-[#5A8A5A] italic mt-0.5">{p.significance}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {helperData.strengths_themes?.length > 0 && (
                    <div className="bg-green-50/50 border border-green-200 rounded-xl p-3" data-testid="helper-strengths">
                      <h4 className="text-xs font-bold text-green-700 mb-2 flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" />Strengths Themes</h4>
                      <ul className="space-y-1">{helperData.strengths_themes.map((s, i) => <li key={i} className="text-xs text-green-800 flex items-start gap-1.5"><span className="text-green-400 mt-1 shrink-0">-</span>{s}</li>)}</ul>
                    </div>
                  )}
                  {helperData.needs_themes?.length > 0 && (
                    <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-3" data-testid="helper-needs">
                      <h4 className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1.5"><TrendingDown className="h-3.5 w-3.5" />Needs Themes</h4>
                      <ul className="space-y-1">{helperData.needs_themes.map((n, i) => <li key={i} className="text-xs text-amber-800 flex items-start gap-1.5"><span className="text-amber-400 mt-1 shrink-0">-</span>{n}</li>)}</ul>
                    </div>
                  )}
                </div>
                {helperData.cross_data_patterns?.length > 0 && (
                  <div className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid="helper-patterns">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-2 flex items-center gap-1.5"><Info className="h-3.5 w-3.5 text-[#3B6A9B]" />Cross-Data Patterns</h4>
                    {helperData.cross_data_patterns.map((p, i) => (
                      <div key={i} className="mb-2 last:mb-0"><p className="text-xs text-[#1C1C1C]">{p.pattern}</p><p className="text-[10px] text-[#595959] italic">{p.sources}</p></div>
                    ))}
                  </div>
                )}
                {helperData.caution_flags?.length > 0 && (
                  <div className="bg-red-50/40 border border-red-200 rounded-xl p-4" data-testid="helper-cautions">
                    <h4 className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1.5"><Flag className="h-3.5 w-3.5" />Caution Flags</h4>
                    {helperData.caution_flags.map((f, i) => (
                      <div key={i} className="mb-2 last:mb-0"><p className="text-xs text-red-800 font-medium">{f.flag}</p><p className="text-[10px] text-red-600">{f.reason}</p></div>
                    ))}
                  </div>
                )}
                {helperData.wording_nudges?.length > 0 && (
                  <div className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid="helper-nudges">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-2 flex items-center gap-1.5"><Lightbulb className="h-3.5 w-3.5 text-amber-500" />Wording Ideas</h4>
                    <div className="flex flex-wrap gap-2">{helperData.wording_nudges.map((n, i) => <span key={i} className="text-xs bg-[#F3F4F1] text-[#1C1C1C] px-2.5 py-1 rounded-lg border border-[#E5E5E5] cursor-default" data-testid={`nudge-${i}`}>{n}</span>)}</div>
                  </div>
                )}
                {helperData.missing_information?.length > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3" data-testid="helper-missing">
                    <h4 className="text-xs font-bold text-gray-600 mb-1.5">Missing Information</h4>
                    <ul className="space-y-0.5">{helperData.missing_information.map((m, i) => <li key={i} className="text-[10px] text-gray-500">- {m}</li>)}</ul>
                  </div>
                )}
                {helperMeta && !helperMeta.error && (
                  <div className="text-[10px] text-[#595959]/60 flex items-center gap-2"><Info className="h-3 w-3" /><span>Sources: {helperMeta.dataSources?.join(', ') || 'none'}{helperMeta.notes ? ` | ${helperMeta.notes}` : ''}</span></div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Evaluator-Specific Interpretations */}
      {interpretations.length === 0 ? (
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 text-center">
          <Users className="h-8 w-8 text-[#E5E5E5] mx-auto mb-3" />
          <p className="text-sm text-[#595959] mb-4">No evaluator interpretations added yet.</p>
          <Button variant="outline" className="rounded-xl" onClick={addInterpretation} data-testid="add-first-interpretation">
            <Plus className="h-4 w-4 mr-2" />Add Evaluator Interpretation
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {interpretations.map((item, idx) => (
            <div key={item.id || idx} className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid={`evaluator-interp-${idx}`}>
              <div className="flex items-center justify-between px-5 py-3.5 bg-[#F3F4F1]">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-[#5A8A5A]" />
                  <span className="text-sm font-medium text-[#1C1C1C]">Evaluator {idx + 1}</span>
                  {item.name && <span className="text-xs text-[#595959]">— {item.name}</span>}
                </div>
                <button onClick={() => removeInterpretation(idx)} className="p-1.5 rounded-lg hover:bg-red-50 transition" data-testid={`remove-interp-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Evaluator Name</label>
                    <Input value={item.name || ''} onChange={e => updateInterpretation(idx, 'name', e.target.value)}
                      placeholder="e.g., Dr. Sarah Martinez" className="rounded-xl border-[#E5E5E5] text-sm h-9"
                      data-testid={`interp-name-${idx}`} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Role / Credentials</label>
                    <Select value={item.role || ''} onValueChange={v => updateInterpretation(idx, 'role', v)}>
                      <SelectTrigger className="rounded-xl text-sm h-9" data-testid={`interp-role-${idx}`}><SelectValue placeholder="Select role" /></SelectTrigger>
                      <SelectContent>{EVALUATOR_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Interpretation</label>
                  <Textarea value={item.interpretation || ''} onChange={e => updateInterpretation(idx, 'interpretation', e.target.value)}
                    placeholder="Provide your professional interpretation of the assessment results within your area of expertise..."
                    rows={8} className="rounded-xl border-[#E5E5E5] text-sm"
                    data-testid={`interp-text-${idx}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Button variant="outline" className="rounded-xl" onClick={addInterpretation} data-testid="add-interpretation-btn">
        <Plus className="h-4 w-4 mr-2" />Add Evaluator Interpretation
      </Button>

      {/* Optional Overall Synthesis */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="interpretation-overall">
        <div className="flex items-center gap-2 mb-3">
          <Brain className="h-4 w-4 text-[#5A8A5A]" />
          <label className="text-sm font-medium text-[#1C1C1C]">Overall Synthesis</label>
          <Badge className="text-[10px] bg-[#F3F4F1] text-[#595959]">Optional</Badge>
        </div>
        <p className="text-xs text-[#595959] mb-3">
          Optional: Combine evaluator interpretations into a unified synthesis of assessment results.
        </p>
        <Textarea
          value={content.interpretation_text || ''}
          onChange={e => setContent(prev => ({ ...prev, interpretation_text: e.target.value }))}
          placeholder="Optional overall synthesis across all evaluators..."
          rows={6} className="rounded-xl border-[#E5E5E5] text-sm"
          data-testid="interpretation-input"
        />
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl" data-testid="interpretation-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Interpretation'}
        </Button>
      </div>
    </div>
  );
}
